/**
 * API service for the Image Quality page
 */
import { API_GATEWAY_PROCESS_NAME } from '../../utils/constants';
import { apiRequest } from '../../services/apiMiddleware';
import { store } from '../../store';
import { selectStationCode } from '../../store/slices/shiftPlanSlice';
import {
  GetAssociateImageQualityInput,
  GetAssociateImageQualityOutput,
  GetEvidencePhotoLinksInput,
  GetEvidencePhotoLinksOutput,
  GetImageQualityReportInput,
  GetImageQualityReportOutput,
  GetImageQualitySiteRankingsInput,
  GetImageQualitySiteRankingsOutput,
} from './model';
import {
  logOperationalMetric,
  MetricType,
  RCC_IMAGE_QUALITY_PAGE,
  GET_IMAGE_QUALITY_SITE_RANKINGS_API,
  GET_IMAGE_QUALITY_REPORT_API,
  GET_ASSOCIATE_IMAGE_QUALITY_API,
  GET_EVIDENCE_PHOTO_LINKS_API,
  logCounterMetric,
} from '../../utils/metric-utils';

/** Metrics are attributed to the operator's own station, not the site being viewed. */
const postToFacade = async <T>(
  resourcePath: string,
  apiName: string,
  request: object
): Promise<T> => {
  const startTime = Date.now();
  const stationCode = selectStationCode(store.getState()) || '';
  const requestPayload = {
    resourcePath,
    httpMethod: 'post',
    processName: API_GATEWAY_PROCESS_NAME,
    requestBody: request,
  };
  try {
    const response = await apiRequest(requestPayload);
    // apiRequest doesn't throw: a failed call comes back as an error status with errorResponse.
    if (response.status && response.status >= 400) {
      throw new Error(response.errorResponse ?? `${apiName} failed`);
    }
    logOperationalMetric(
      startTime,
      stationCode,
      RCC_IMAGE_QUALITY_PAGE,
      apiName,
      MetricType.SUCCESS
    );
    return response.data;
  } catch (error: any) {
    logOperationalMetric(
      startTime,
      stationCode,
      RCC_IMAGE_QUALITY_PAGE,
      apiName,
      MetricType.FAILED,
      error
    );
    throw error;
  }
};

/** All reporting sites ranked by pass rate for one operating day + shift. */
export const getImageQualitySiteRankings = (
  request: GetImageQualitySiteRankingsInput
): Promise<GetImageQualitySiteRankingsOutput> =>
  postToFacade('/getImageQualitySiteRankings', GET_IMAGE_QUALITY_SITE_RANKINGS_API, request);

/** One site's rollup of evidence image quality for the same window. */
export const getImageQualityReport = (
  request: GetImageQualityReportInput
): Promise<GetImageQualityReportOutput> =>
  postToFacade('/getImageQualityReport', GET_IMAGE_QUALITY_REPORT_API, request);

/** One associate's scored photos for the same window, worst first. */
export const getAssociateImageQuality = (
  request: GetAssociateImageQualityInput
): Promise<GetAssociateImageQualityOutput> =>
  postToFacade('/getAssociateImageQuality', GET_ASSOCIATE_IMAGE_QUALITY_API, request);

/** Short-lived links for one page of evidence photos, fetched in one batch. */
export const getEvidencePhotoLinks = (
  request: GetEvidencePhotoLinksInput
): Promise<GetEvidencePhotoLinksOutput> =>
  postToFacade('/getEvidencePhotoLinks', GET_EVIDENCE_PHOTO_LINKS_API, request);

/** Counts a page view or drawer open for the click dashboards (per station, no user identity). */
export const logImageQualityAction = (action: string): void => {
  logCounterMetric(selectStationCode(store.getState()) || '', RCC_IMAGE_QUALITY_PAGE, action);
};
